import { Box, Typography } from "@mui/material";
import React from "react";

/** Raw `source` tags a message can carry across the 3-tier read. `'database'`
 *  and `'backup'` are aliases for the server DB tier; live/undefined = WhatsApp. */
export type MessageSource =
    | 'whatsapp'
    | 'vault'
    | 'database'
    | 'backup'
    | 'db'
    | null
    | undefined;

type SourceBadgeProps = {
    source?: MessageSource;
    size?: 'small' | 'medium';
}

/** Map any raw source tag to a badge {label, color}. Three tiers:
 *  - WA (green)     = live WhatsApp store
 *  - VAULT (amber)  = client-side IndexedDB vault
 *  - DB (gray)      = server DB backup */
const badgeFor = (source: MessageSource): { label: string; bg: string } | null => {
    switch (source) {
        case 'whatsapp':
            return { label: 'WA', bg: '#25D366' };
        case 'vault':
            return { label: 'VAULT', bg: '#f59e0b' };
        case 'database':
        case 'backup':
        case 'db':
            return { label: 'DB', bg: '#9e9e9e' };
        default:
            return null;
    }
};

/**
 * SourceBadge displays a small badge indicating which tier a message came from
 * (WhatsApp live / local vault / server DB). Debug aid — callers gate it on the
 * `bt-debug` flag.
 */
const SourceBadge = React.memo(({ source, size = 'small' }: SourceBadgeProps) => {
    const badge = badgeFor(source);
    if (!badge) return null;

    const fontSize = size === 'small' ? '0.6rem' : '0.7rem';
    const padding = size === 'small' ? '1px 4px' : '2px 6px';

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: badge.bg,
                color: 'white',
                borderRadius: '3px',
                padding: padding,
                marginLeft: '4px',
                lineHeight: 1,
            }}
        >
            <Typography
                component="span"
                sx={{
                    fontSize: fontSize,
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                }}
            >
                {badge.label}
            </Typography>
        </Box>
    );
});

export default SourceBadge;
